import { memo } from 'react';
import {
  Select as RACSelect,
  type SelectProps as RACSelectProps,
  SelectValue as RACSelectValue,
  type SelectValueProps as RACSelectValueProps,
} from 'react-aria-components';

import { merge } from '../utils';
import { Text } from './Text';

type SelectionMode = 'single' | 'multiple';
export type SelectProps<T extends object = object, M extends SelectionMode = 'single'> = RACSelectProps<T, M>;

export const ISelect = <T extends object = object, M extends SelectionMode = 'single'>({
  className,
  children,
  ...rest
}: SelectProps<T, M>) => {
  /* prettier-ignore */
  const styles = [
    'bg-white',
    'data-[disabled]:cursor-not-allowed',
    'font-inter',
    'group/select',
    'outline-none',
    'text-gray-600',
    'text-sm',
  ];

  return (
    <RACSelect aria-label="select" className={merge(styles, className)} {...rest}>
      {children}
    </RACSelect>
  );
};

export const Select = memo(ISelect) as typeof ISelect;

const ISelectValue = <T extends object = object>({ children, className, ...rest }: SelectValueProps<T>) => {
  return (
    <RACSelectValue
      className={merge('group/select-value flex grow items-center justify-start gap-1', className)}
      {...rest}
    >
      {children}
    </RACSelectValue>
  );
};

export type SelectValueProps<T extends object = object> = RACSelectValueProps<T>;
export const SelectValue = memo(ISelectValue) as typeof ISelectValue;

export interface PillsSelectValueProps<T> {
  children?: (item?: T | null) => React.ReactNode;
}

export function PillSelectValue<T extends object>({ children }: PillsSelectValueProps<T>) {
  return (
    <SelectValue<T>>
      {({ selectedItems, isPlaceholder }) =>
        isPlaceholder ? (
          <Text>Select value</Text>
        ) : typeof children === 'function' ? (
          selectedItems.map(children)
        ) : (
          children
        )
      }
    </SelectValue>
  );
}
